<script setup>
import RecordLink from '@/components/record/RecordLink.vue'
import { computed, ref } from 'vue'
import { api } from '@/lib/http.js'
import { formatMoney, formatNumber } from '@/lib/format.js'
import { useMeta } from '@/stores/meta.js'
import { reportError } from '@/composables/useSave.js'
import RefPicker from '@/components/form/RefPicker.vue'
import UiButton from '@/components/ui/UiButton.vue'

/**
 * What a campaign brought (D-139): leads, opportunities, value won against what it cost, the
 * return; and the marketing lists it runs on, linked and unlinked here.
 */
const props = defineProps({ record: { type: Object, required: true }, canLink: Boolean })
const emit = defineEmits(['changed'])
const meta = useMeta()
const r = computed(() => props.record.results || {})
const adding = ref(false)
const list = ref(null)

async function link() {
  try {
    await api(`/campaigns/${props.record.uuid}/marketing-lists/${list.value}`, { method: 'POST' })
    adding.value = false
    list.value = null
    emit('changed')
  } catch (e) {
    reportError(e)
  }
}
async function unlink(l) {
  try {
    await api(`/campaigns/${props.record.uuid}/marketing-lists/${l.uuid}`, { method: 'DELETE' })
    emit('changed')
  } catch (e) {
    reportError(e)
  }
}
const money = (v) => formatMoney(v || 0, meta.baseCurrency)
</script>

<template>
  <section class="card" data-testid="campaign-results">
    <h3 class="border-b border-default px-3 py-2 text-sm font-semibold">{{ $t('marketing.results') }}</h3>
    <dl class="grid grid-cols-2 gap-px bg-[var(--border)] sm:grid-cols-4">
      <div class="surface px-3 py-2"><dt class="text-xs text-muted">{{ $t('marketing.leads') }}</dt><dd class="text-lg font-semibold tabular-nums">{{ r.leads || 0 }}</dd><dd class="text-xs text-faint">{{ $t('marketing.qualified', { n: r.leads_qualified || 0 }) }}</dd></div>
      <div class="surface px-3 py-2"><dt class="text-xs text-muted">{{ $t('marketing.opportunities') }}</dt><dd class="text-lg font-semibold tabular-nums">{{ r.opportunities || 0 }}</dd><dd class="text-xs text-faint">{{ $t('marketing.wonLost', { won: r.opportunities_won || 0, lost: r.opportunities_lost || 0 }) }}</dd></div>
      <div class="surface px-3 py-2"><dt class="text-xs text-muted">{{ $t('marketing.wonValue') }}</dt><dd class="text-lg font-semibold tabular-nums">{{ money(r.won_value) }}</dd><dd class="text-xs text-faint">{{ $t('marketing.pipeline', { value: money(r.pipeline_value) }) }}</dd></div>
      <div class="surface px-3 py-2"><dt class="text-xs text-muted">{{ $t('marketing.roi') }}</dt><dd :class="['text-lg font-semibold tabular-nums', r.roi === null || r.roi === undefined ? '' : r.roi >= 0 ? 'text-emerald-600' : 'text-red-600']">{{ r.roi === null || r.roi === undefined ? '—' : `${formatNumber(r.roi)}%` }}</dd><dd class="text-xs text-faint">{{ $t('marketing.cost', { value: money(r.cost) }) }}</dd></div>
    </dl>
  </section>
  <section class="card" data-testid="campaign-lists">
    <header class="flex items-center gap-2 border-b border-default px-3 py-2">
      <h3 class="flex-1 text-sm font-semibold">{{ $t('marketing.lists') }} <span class="font-normal text-faint">({{ (record.marketing_lists || []).length }})</span></h3>
      <UiButton v-if="canLink" size="xs" variant="ghost" icon="fa-plus" :aria-label="$t('common.add')" @click="adding = !adding" />
    </header>
    <div v-if="adding" class="flex gap-2 border-b border-default p-3">
      <RefPicker v-model="list" entity="marketing_list" class="flex-1" />
      <UiButton variant="primary" :disabled="!list" @click="link">{{ $t('common.add') }}</UiButton>
    </div>
    <p v-if="!(record.marketing_lists || []).length" class="px-3 py-3 text-sm text-faint">{{ $t('common.empty') }}</p>
    <ul v-else class="divide-y divide-[var(--border)]">
      <li v-for="l in record.marketing_lists" :key="l.uuid" class="flex items-center gap-2 px-3 py-2 text-sm">
        <RecordLink class="link min-w-0 flex-1 truncate" entity="marketing_list" :uuid="l.uuid">{{ l.label }}</RecordLink>
        <span class="text-xs text-faint">{{ $t(`marketing.memberTypes.${l.member_type}`) }} · {{ $t(`marketing.listTypes.${l.list_type}`) }}</span>
        <UiButton v-if="canLink" size="xs" variant="ghost" icon="fa-xmark" class="hover:text-red-600" :aria-label="$t('common.remove')" @click="unlink(l)" />
      </li>
    </ul>
  </section>
</template>
